import { css, FlattenSimpleInterpolation } from 'styled-components';

type ScreenSizes = {
  giant: number;
  desktop: number;
  tablet: number;
  phone: number;
};

type ScreenFunction = (
  ...args: [TemplateStringsArray]
) => FlattenSimpleInterpolation;

type ScreenFunctions = {
  giant: ScreenFunction;
  desktop: ScreenFunction;
  tablet: ScreenFunction;
  phone: ScreenFunction;
};

type ScreenType = keyof ScreenFunctions;

// sizes for devices
export const sizes: ScreenSizes = {
  giant: 1440,
  desktop: 1024,
  tablet: 668,
  phone: 420,
};

/**
 * return and object like this
 * {
 *   giant: () => {}, // return the CSS media query `@media( max-width: 90em ) { ..CSS rules.. }`
 *   desktop: () => {},
 *   ...
 * }
 * we use it in this way inside of styled-components template function ``
 * apply these CSS for smaller than tablet
 *
 * const Button = styled.button`
 *  ${screenSmallerThan.tablet`
 *    font-size: 13px;
 *  `}
 * `
 * this function transform to CSS media query inside of class
 * @media (max-width: 48em) {
 *   font-size: 13px;
 * }
 *
 */
export const screenSmallerThan = (
  Object.keys(sizes) as Array<keyof ScreenFunction>
).reduce((accumulator: ScreenFunctions, label: ScreenType) => {
  // calculate the em size based on paragraph font size 16px
  const emSize = sizes[label] / 16;
  accumulator[label] = (
    ...args: [TemplateStringsArray]
  ): FlattenSimpleInterpolation => css`
    @media (max-width: ${emSize}em) {
      ${css(...args)};
    }
  `;
  return accumulator;
}, {} as ScreenFunctions);

/**
 * If We follow first mobile design so we usually use this function
 * to write media query in styled components
 * same as screenSmallerThan just return an object with different functions
 * based on the screen that returns `@media (min-width: XX) {}` css
 *
 * apply these CSS for larger than that screen size
 */
export const screenLargerThan = (
  Object.keys(sizes) as Array<keyof ScreenFunction>
).reduce((accumulator: ScreenFunctions, label: ScreenType) => {
  const emSize = sizes[label] / 16;
  accumulator[label] = (
    ...args: [TemplateStringsArray]
  ): FlattenSimpleInterpolation => css`
    @media (min-width: ${emSize}em) {
      ${css(...args)};
    }
  `;
  return accumulator;
}, {} as ScreenFunctions);
